import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../../services/ApiService";

/* ── Logo ──────────────────────────────────────────────────── */
const Logo = ({ size = 38 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 140 140" fill="none">
    <defs>
      <linearGradient id="bl-ng" x1="30" y1="15" x2="110" y2="125">
        <stop offset="0%" stopColor="#00FFE0" /><stop offset="50%" stopColor="#00E5CC" /><stop offset="100%" stopColor="#22D97F" />
      </linearGradient>
    </defs>
    <rect width="140" height="140" rx="30" fill="#0F2140" />
    <path d="M70 18L28 37v32c0 26 42 45 42 45s42-19 42-45V37L70 18z" fill="none" stroke="url(#bl-ng)" strokeWidth="4" />
    <circle cx="70" cy="60" r="16" fill="none" stroke="url(#bl-ng)" strokeWidth="2.5" opacity="0.35" />
    <text x="70" y="68" textAnchor="middle" fontFamily="Sora,sans-serif" fontWeight="800" fontSize="20" fill="url(#bl-ng)">J</text>
  </svg>
);

const Ico = {
  arrowLeft: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M19 12H5" /><path d="M12 19l-7-7 7-7" /></svg>,
  arrowRight: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M5 12h14" /><path d="M12 5l7 7-7 7" /></svg>,
  search: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></svg>,
  calendar: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4" /><path d="M8 2v4" /><path d="M3 10h18" /></svg>,
};

interface Blog {
  id: string;
  _id?: string;
  slug: string;
  title: string;
  coverImage?: string | null;
  content?: string;
  excerpt?: string;
  read_time?: string;
  createdAt?: string;
  author?: { name: string } | string;
  category?: string;
}

/* ── Helpers (module-scope so CoverImage can use them) ─────── */
function stripHtml(html: string = ""): string {
  const div = document.createElement("div");
  div.innerHTML = html;
  return div.textContent || div.innerText || "";
}

function cleanTitle(raw = ""): string {
  const cleaned = stripHtml(raw).replace(/\s+/g, " ").trim();
  return cleaned || "Untitled";
}

function getMediaUrl(url?: string | null): string {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  const base = process.env.REACT_APP_MEDIA_BASE || "";
  if (url.startsWith("/uploads/")) return `${base}${url}`;
  return `${base}/uploads/blogs/${url}`;
}

// Extract first <img src="..."> URL from HTML. Returns "" if none.
function extractFirstImage(html: string = ""): string {
  if (!html) return "";
  const match = html.match(/<img[^>]+src=["']([^"']+)["']/i);
  return match ? match[1] : "";
}

function formatDate(dateStr?: string): string {
  if (!dateStr) return "";
  try {
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: "numeric", month: "short", day: "numeric"
    });
  } catch { return ""; }
}

function getExcerpt(blog: Blog): string {
  const fromExcerpt = stripHtml(blog.excerpt || "").trim();
  if (fromExcerpt) return fromExcerpt.slice(0, 120) + (fromExcerpt.length > 120 ? "..." : "");
  const fromContent = stripHtml(blog.content || "").trim();
  if (fromContent) return fromContent.slice(0, 120) + (fromContent.length > 120 ? "..." : "");
  return "Read more →";
}

/* ── Smart cover image with URL cascade ──────────────────────
 * Tries cover URL first; if it fails (404, CORS, missing file),
 * falls back to first <img> found in content. If none work, shows
 * placeholder. Each card has its own state.
 * ────────────────────────────────────────────────────────────── */
function CoverImage({ blog, alt }: { blog: Blog; alt: string }) {
  // Build ordered list of URLs to try
  const urls: string[] = [];
  if (blog.coverImage) urls.push(getMediaUrl(blog.coverImage));
  const contentImg = extractFirstImage(blog.content);
  if (contentImg) urls.push(getMediaUrl(contentImg));

  const [idx, setIdx] = useState(0);

  // No URLs at all, or exhausted all URLs → placeholder
  if (urls.length === 0 || idx >= urls.length) {
    return <div className="bl-placeholder"><span>📝</span></div>;
  }

  const currentUrl = urls[idx];
  const isVideo = currentUrl.endsWith(".mp4") || currentUrl.endsWith(".mov");

  if (isVideo) {
    return (
      <video
        src={currentUrl}
        className="bl-media"
        controls
        onError={() => setIdx(idx + 1)}
      />
    );
  }

  return (
    <img
      src={currentUrl}
      className="bl-media"
      alt={alt}
      onError={() => setIdx(idx + 1)}
    />
  );
}

/* ══════════════════════════════════════════════════════════════ */
export default function BlogPublicList() {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState<Blog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");

  useEffect(() => {
    async function load() {
      try {
        setLoading(true);
        setError(null);
        const res = (await api.get("/public/blogs")) as any;
        const list: Blog[] =
          res.blogs ?? res.data?.blogs ?? res.data?.data ?? (Array.isArray(res.data) ? res.data : []);
        setBlogs(list.map((b: any) => ({ ...b, id: b.id || b._id || "" })));
      } catch (err) {
        console.error("Blog load error:", err);
        setError("Failed to load blogs");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const categories = ["all", ...Array.from(new Set(blogs.map(b => b.category || "").filter(Boolean)))];

  const filtered = blogs.filter(blog => {
    const s = searchTerm.toLowerCase();
    const matchSearch = cleanTitle(blog.title).toLowerCase().includes(s) || stripHtml(blog.excerpt || "").toLowerCase().includes(s);
    const matchCat = selectedCategory === "all" || blog.category === selectedCategory;
    return matchSearch && matchCat;
  });

  const sorted = [...filtered].sort((a, b) =>
    new Date(b.createdAt || "").getTime() - new Date(a.createdAt || "").getTime()
  );

  return (
    <div className="bl-root">
      <style dangerouslySetInnerHTML={{ __html: BL_STYLES }} />

      {/* NAV */}
      <nav className="bl-nav">
        <a href="#" className="bl-nav-logo" onClick={e => { e.preventDefault(); navigate('/'); }}>
          <Logo size={36} /><span className="bl-wm"><span className="jep">Jep</span><span className="tags">tags</span></span>
        </a>
        <button className="bl-back" onClick={() => navigate(-1)}>
          <span className="bl-ico-16">{Ico.arrowLeft}</span> Back
        </button>
      </nav>

      {/* HERO */}
      <section className="bl-hero">
        <div className="bl-badge">Blog</div>
        <h1>JepTags <span className="grad">Blog</span></h1>
        <p>Articles about anti-counterfeit, traceability, supply chain, and technology.</p>

        {/* Search */}
        <div className="bl-search-wrap">
          <span className="bl-search-ico">{Ico.search}</span>
          <input type="text" placeholder="Search articles..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
        </div>
      </section>

      {/* Category Filter */}
      {categories.length > 1 && (
        <div className="bl-cats">
          {categories.map(cat => (
            <button key={cat} className={`bl-cat ${selectedCategory === cat ? 'active' : ''}`} onClick={() => setSelectedCategory(cat)}>
              {cat === "all" ? "All" : cat}
            </button>
          ))}
        </div>
      )}

      {/* LOADING / ERROR */}
      {loading && <div className="bl-msg">Loading...</div>}
      {error && <div className="bl-msg error">{error}</div>}

      {/* NO RESULTS */}
      {!loading && !error && sorted.length === 0 && (
        <div className="bl-msg">No blog posts found.</div>
      )}

      {/* BLOG GRID */}
      {!loading && !error && sorted.length > 0 && (
        <section className="bl-grid-section">
          <div className="bl-grid">
            {sorted.map(blog => {
              const title = cleanTitle(blog.title);
              return (
                <Link key={blog.id} to={`/blog/${blog.slug}`} className="bl-card">
                  <div className="bl-card-img">
                    <CoverImage blog={blog} alt={title} />
                  </div>
                  <div className="bl-card-body">
                    {blog.category && <span className="bl-card-cat">{blog.category}</span>}
                    <h2>{title}</h2>
                    <p>{getExcerpt(blog)}</p>
                    <div className="bl-card-footer">
                      {blog.createdAt && (
                        <span className="bl-card-date">
                          <span className="bl-ico-14">{Ico.calendar}</span>
                          {formatDate(blog.createdAt)}
                        </span>
                      )}
                      <span className="bl-card-arrow">{Ico.arrowRight}</span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════ */
const BL_STYLES = `
.bl-root *,.bl-root *::before,.bl-root *::after{margin:0;padding:0;box-sizing:border-box}
.bl-root{
  --cyan:#00E5CC;--cyan-bright:#00FFE0;--teal:#22D97F;
  --navy-deep:#030a14;--navy:#0a1628;--navy-mid:#0F2140;
  font-family:'Sora',sans-serif;background:var(--navy-deep);color:#e0f0f0;overflow-x:hidden;min-height:100vh;
}
.bl-root a{text-decoration:none;color:inherit}
.grad{background:linear-gradient(135deg,var(--cyan-bright),var(--cyan),var(--teal));-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text}
.jep{color:var(--cyan)}.tags{color:var(--teal)}
.bl-ico-16 svg{width:16px;height:16px}.bl-ico-14 svg{width:14px;height:14px}

/* NAV */
.bl-nav{position:fixed;top:0;left:0;right:0;z-index:100;padding:16px 40px;display:flex;align-items:center;justify-content:space-between;backdrop-filter:blur(20px);background:rgba(3,10,20,0.85);border-bottom:1px solid rgba(0,229,204,0.06)}
.bl-nav-logo{display:flex;align-items:center;gap:12px;text-decoration:none;cursor:pointer}
.bl-wm{font-size:22px;font-weight:700;letter-spacing:-.5px}
.bl-back{display:flex;align-items:center;gap:6px;background:none;border:1px solid rgba(0,229,204,0.15);color:rgba(224,240,240,0.6);font-family:'Sora',sans-serif;font-size:13px;padding:8px 16px;border-radius:8px;cursor:pointer;transition:all .3s}
.bl-back:hover{border-color:var(--cyan);color:var(--cyan)}

/* HERO */
.bl-hero{padding:140px 40px 48px;text-align:center;max-width:720px;margin:0 auto}
.bl-badge{display:inline-block;padding:8px 20px;border-radius:100px;background:rgba(0,229,204,0.08);border:1px solid rgba(0,229,204,0.15);font-size:12px;font-weight:600;letter-spacing:1.5px;text-transform:uppercase;color:var(--cyan);margin-bottom:24px}
.bl-hero h1{font-size:clamp(32px,5vw,52px);font-weight:800;line-height:1.1;letter-spacing:-2px;margin-bottom:16px}
.bl-hero p{font-size:17px;line-height:1.7;color:rgba(224,240,240,0.5);max-width:560px;margin:0 auto 32px}

/* SEARCH */
.bl-search-wrap{position:relative;max-width:560px;margin:0 auto}
.bl-search-ico{position:absolute;left:18px;top:50%;transform:translateY(-50%);width:20px;height:20px;color:rgba(224,240,240,0.25);display:flex}
.bl-search-ico svg{width:20px;height:20px}
.bl-search-wrap input{width:100%;padding:16px 20px 16px 52px;border-radius:14px;border:1.5px solid rgba(0,229,204,0.1);background:rgba(15,33,64,0.5);color:#e0f0f0;font-family:'Sora',sans-serif;font-size:15px;outline:none;transition:all .3s}
.bl-search-wrap input::placeholder{color:rgba(224,240,240,0.2)}
.bl-search-wrap input:focus{border-color:var(--cyan);box-shadow:0 0 0 3px rgba(0,229,204,0.1)}

/* CATEGORIES */
.bl-cats{display:flex;flex-wrap:wrap;justify-content:center;gap:10px;padding:0 24px;margin-bottom:48px}
.bl-cat{padding:8px 20px;border-radius:100px;font-family:'Sora',sans-serif;font-size:13px;font-weight:600;cursor:pointer;border:1px solid rgba(0,229,204,0.1);background:rgba(15,33,64,0.3);color:rgba(224,240,240,0.5);transition:all .3s}
.bl-cat:hover{border-color:rgba(0,229,204,0.2);color:rgba(224,240,240,0.7)}
.bl-cat.active{background:linear-gradient(135deg,var(--cyan-bright),var(--teal));color:var(--navy-deep);border-color:transparent;font-weight:700}

/* MSG */
.bl-msg{text-align:center;padding:60px 24px;color:rgba(224,240,240,0.35);font-size:16px}
.bl-msg.error{color:#f87171}

/* GRID */
.bl-grid-section{max-width:1100px;margin:0 auto;padding:0 24px 100px}
.bl-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:24px}

/* CARD */
.bl-card{border-radius:20px;background:rgba(15,33,64,0.5);border:1px solid rgba(0,229,204,0.06);overflow:hidden;transition:all .4s;display:flex;flex-direction:column}
.bl-card:hover{border-color:rgba(0,229,204,0.15);transform:translateY(-4px);box-shadow:0 20px 60px rgba(0,0,0,0.3)}
.bl-card-img{height:200px;overflow:hidden;position:relative}
.bl-media{width:100%;height:100%;object-fit:cover;transition:transform .5s}
.bl-card:hover .bl-media{transform:scale(1.05)}
.bl-placeholder{height:100%;background:rgba(0,229,204,0.04);display:flex;align-items:center;justify-content:center;font-size:36px}
.bl-card-body{padding:24px;display:flex;flex-direction:column;flex:1}
.bl-card-cat{font-size:10px;font-weight:700;letter-spacing:1px;text-transform:uppercase;color:var(--cyan);margin-bottom:8px}
.bl-card-body h2{font-size:18px;font-weight:700;line-height:1.4;margin-bottom:10px}
.bl-card-body p{font-size:13px;line-height:1.6;color:rgba(224,240,240,0.4);flex:1;margin-bottom:16px}
.bl-card-footer{display:flex;align-items:center;justify-content:space-between}
.bl-card-date{display:flex;align-items:center;gap:6px;font-size:12px;color:rgba(224,240,240,0.3)}
.bl-card-arrow{width:18px;height:18px;color:var(--cyan);display:flex}
.bl-card-arrow svg{width:18px;height:18px}

/* RESPONSIVE */
@media(max-width:900px){
  .bl-nav{padding:14px 20px}
  .bl-hero{padding:120px 24px 36px}
  .bl-grid{grid-template-columns:1fr 1fr}
}
@media(max-width:600px){
  .bl-grid{grid-template-columns:1fr}
}
`;